import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Typography, makeStyles } from '@material-ui/core'
import SelectionScopeBar from 'components/SelectionTree/SelectionScopeBar'
import React, { useState } from 'react'
import DivisionSectionTree from './DivisionSectionTree'
import SpecIntelligenceAnalysisFooter from './SpecIntelligenceAnalysisFooter'
import { MAX_RECOMMENDED_SECTIONS, TOKEN_LIMIT_WARNING_MESSAGE } from './specIntelligenceConstants'

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
    },
    treeScroll: {
        flex: 1,
        overflow: 'auto',
    },
    tokenLimitWarning: {
        fontSize: pxToRem(12),
        color: '#6B5017',
        backgroundColor: '#FFF3CD',
        borderRadius: pxToRem(4),
        margin: `0 ${pxToRem(16)}`,
        padding: `${pxToRem(8)} ${pxToRem(16)}`,
    },
}))

/** Props for {@link SpecIntelligencePickerView}. */
export interface SpecIntelligencePickerViewProps {
    /** Called when the user clicks Run Analysis; receives the currently selected section IDs. */
    onRunAnalysis: (ids: string[]) => void
    /**
     * Section numbers (e.g. `['07 26 00', '03 30 00']`) to pre-select when the
     * tree first loads. Typically the sections from the previous report when
     * the user clicks Re-run.
     */
    initialSectionNumbers?: string[]
}

/**
 * Section-picker view inside the Spec Intelligence drawer — scope bar, section
 * tree, and Run Analysis footer. Owns selection state internally.
 */
const SpecIntelligencePickerView = ({ onRunAnalysis, initialSectionNumbers }: SpecIntelligencePickerViewProps) => {
    const classes = useStyles()
    const [selectedLeafIds, setSelectedLeafIds] = useState<string[]>([])
    const [allLeafIds, setAllLeafIds] = useState<string[]>([])
    const totalCount = allLeafIds.length
    const selectedCount = selectedLeafIds.length

    return (
        <div className={classes.root}>
            {totalCount > 0 && (
                <SelectionScopeBar
                    label="SCOPE"
                    selectedCount={selectedCount}
                    totalCount={totalCount}
                    itemLabel="sections"
                    onSelectAll={() => setSelectedLeafIds(allLeafIds)}
                    onUnselectAll={() => setSelectedLeafIds([])}
                />
            )}
            <div className={classes.treeScroll}>
                <DivisionSectionTree
                    selectedLeafIds={selectedLeafIds}
                    onSelectionChange={setSelectedLeafIds}
                    onNodesLoaded={setAllLeafIds}
                    initialSectionNumbers={initialSectionNumbers}
                    scrollToFirstSelected={!!initialSectionNumbers?.length}
                />
            </div>
            {selectedCount > MAX_RECOMMENDED_SECTIONS && (
                <Typography className={classes.tokenLimitWarning}>
                    {TOKEN_LIMIT_WARNING_MESSAGE}
                </Typography>
            )}
            <SpecIntelligenceAnalysisFooter
                selectedCount={selectedCount}
                onRunAnalysis={() => onRunAnalysis(selectedLeafIds)}
            />
        </div>
    )
}

export default SpecIntelligencePickerView
